import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@truefit/http-utils';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';

import type { GameCamera } from './useGameCamerasQuery';
import { getGameCamerasQueryKey } from './useGameCamerasQuery';
import { getQueryKey as getCamerasQueryKey } from '@/features/camera/hooks/useCamerasQuery';

export interface UpdateGameCameraData {
  positionX?: number;
  positionY?: number;
  positionZ?: number;
  forwardX?: number;
  forwardY?: number;
  forwardZ?: number;
  tilt?: number;
  roll?: number;
  displayName?: string;
  isLive?: boolean;
  fovMax?: number;
  fovMin?: number;
  distMax?: number;
  distMin?: number;
  offset?: number;
  version?: number;
  statusMessage?: string | null;
}

export const useUpdateGameCameraMutation = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ 
      cameraId,
      data,
      gameId
    }: { 
      cameraId: number;
      data: UpdateGameCameraData;
      gameId: string | number;
    }) => {
      // Simplified: always use the gameId/cameraId endpoint
      // Each game now has its own gameCamera records, so this endpoint always works
      const response = await put<GameCamera>(`/v1.0/games/${gameId}/cameras/${cameraId}`, data);
      return { gameId, data: response.data };
    },
    onMutate: async ({ cameraId, data, gameId }) => {
      if (!gameId) return;

      // Cancel outgoing refetches
      const queryKey = getGameCamerasQueryKey(gameId);
      await queryClient.cancelQueries({ queryKey });

      // Snapshot previous value for rollback on error
      const previousData = queryClient.getQueryData<GameCamera[]>(queryKey);

      // Don't do optimistic updates - wait for server response with new version
      // This prevents stale version issues on consecutive updates

      return { previousData, queryKey };
    },
    onSuccess: ({ gameId, data: updatedCamera }) => {
      if (gameId) {
        // Match on the gameCamera's own id — cameraId is nullable now, so a
        // null-vs-null comparison would replace every template-created camera.
        const queryKey = getGameCamerasQueryKey(gameId);
        queryClient.setQueryData<GameCamera[]>(queryKey, prev =>
          prev?.map(camera =>
            camera.id === updatedCamera.id ? updatedCamera : camera
          ) || []
        );
        queryClient.invalidateQueries({ queryKey: getCamerasQueryKey(String(gameId)) });
      }
      
      enqueueSnackbar('Camera updated successfully', { variant: 'success' });
    },
    onError: (error: AxiosError<any>, variables, context) => {
      // Revert on error
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }

      if (error.response?.status === 409) {
        enqueueSnackbar(
          'Camera was modified by another user. Please reload and try again.',
          { variant: 'warning', autoHideDuration: 6000 }
        );
        
        if (variables.gameId) {
          queryClient.invalidateQueries({ queryKey: getGameCamerasQueryKey(variables.gameId) });
          queryClient.invalidateQueries({ queryKey: getCamerasQueryKey(String(variables.gameId)) });
        }
      } else {
        const message = error?.response?.data?.message || error?.message || 'Failed to update camera';
        enqueueSnackbar(message, { variant: 'error' });
      }
    },
  });
};

export default useUpdateGameCameraMutation;